import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AppButton } from '../../button/app-button';
import { useMediaQuery } from '../../hooks/use-media-query';
import { up } from '../../../style-utils/media-queries';
import { DayNumber, FocusCause, MonthGrid } from '../month-grid/month-grid';
import {
  addMonthKey,
  fullDayName,
  monthOf,
  monthsBetween,
} from '../month-grid/month-model';
import {
  ConflictLine,
  Header,
  Label,
  Mark,
  Months,
  Nav,
  Root,
  Status,
  WarningLine,
  ZoneNote,
} from './date-range-picker.styled';
import {
  DateRangeValue,
  OccupiedRange,
  activateDay,
  conflictText,
  liveText,
  markOf,
  markText,
  rangeConflicts,
  rangeFromDrag,
} from './range-model';

export type { DateRangeValue, OccupiedRange, OccupiedKind } from './range-model';

/**
 * CHECK-IN TO CHECK-OUT — the Stays date-range picker, on two MonthGrids.
 *
 * `value.end` is the CHECK-OUT day: the range is half-open, so the nights are
 * start … end − 1 and a stay's check-out day is a free check-in day.
 *
 * Tap (or click, or Enter) once for check-in, then a later day for check-out;
 * a day on or before check-in starts again there, and so does any tap once
 * both are set. A mouse drag sets both at once. Touch never drags.
 *
 * Booked, closed and channel-held nights are MARKED, never disabled: a person
 * may need to see the clash to resolve it, and the conflict line says what it
 * is. Stays and owner blocks are conflicts (role=alert, onConflictChange); a
 * channel's unavailable nights only warn.
 *
 * Two months side by side from md up, one below. ONE focus cursor across both
 * grids (and ONE tab stop): PageUp / PageDown move the pair a month at a time.
 *
 * No <input>, no hover dependency: nothing here needs a keyboard to open on a
 * phone, and every target is 44px under a thumb.
 */

export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
  /** Today on the building's calendar. */
  today: string;
  /** 'YYYY-MM' to open on; default the check-in's month, else today's. */
  initialMonth?: string;
  minDate?: string;
  maxDate?: string;
  /** Half-open ranges already on the calendar. */
  occupied: ReadonlyArray<OccupiedRange>;
  onConflictChange?: (conflicts: OccupiedRange[]) => void;
  /** The group's name, e.g. "Stay dates". */
  label: string;
  id: string;
  /** Which clock these days are on, when it is not the reader's. */
  zoneNote?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

/* Two months from md up — the media string without its "@media " head. */
const WIDE_QUERY = up('md').replace(/^@media\s*/, '');

const hasMatchMedia = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function';

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  today,
  initialMonth,
  minDate,
  maxDate,
  occupied,
  onConflictChange,
  label,
  id,
  zoneNote,
  disabled = false,
  className,
}) => {
  /* No matchMedia (jsdom, very old engines): the wide, two-month layout. */
  const wideMatch = useMediaQuery(WIDE_QUERY);
  const count = !hasMatchMedia() || wideMatch ? 2 : 1;

  const [firstMonth, setFirstMonth] = useState<string>(
    () => initialMonth ?? monthOf(value.start ?? today)
  );
  const [cursor, setCursor] = useState<string>(() => value.start ?? today);
  /* Where a drag (or a Shift+arrow run) is anchored. */
  const anchor = useRef<string | null>(null);

  const months = count === 2 ? [firstMonth, addMonthKey(firstMonth, 1)] : [firstMonth];
  const lastMonth = months[months.length - 1];
  const labelId = `${id}-label`;
  const statusId = `${id}-status`;

  const { conflicts, warnings } = useMemo(
    () => rangeConflicts(value, occupied),
    [value, occupied]
  );

  /* onConflictChange: once on mount, then whenever the LIST changes — not on
     every render that rebuilds an equal array. */
  const conflictKey = conflicts
    .map(range => `${range.kind}|${range.start}|${range.end}|${range.label}`)
    .join('\n');
  const reportConflicts = useRef(onConflictChange);
  reportConflicts.current = onConflictChange;
  const latestConflicts = useRef(conflicts);
  latestConflicts.current = conflicts;
  useEffect(() => {
    reportConflicts.current?.(latestConflicts.current);
  }, [conflictKey]);

  const dayDisabled = (day: string): boolean =>
    disabled || (!!minDate && day < minDate) || (!!maxDate && day > maxDate);

  const canGoBack =
    !disabled && (!minDate || addMonthKey(firstMonth, -1) >= monthOf(minDate));
  const canGoForward =
    !disabled && (!maxDate || addMonthKey(lastMonth, 1) <= monthOf(maxDate));

  const commit = (next: DateRangeValue) => {
    if (next.start === value.start && next.end === value.end) return;
    onChange(next);
  };

  const onFocusDayChange = (day: string, cause: FocusCause, from: string) => {
    const target = monthOf(day);
    if (cause === 'page') {
      /* PageUp / PageDown move the PAIR, so the cursor keeps its column. */
      const shift = monthsBetween(monthOf(from), target);
      setFirstMonth(prev => addMonthKey(prev, shift));
    } else if (target < firstMonth) {
      setFirstMonth(target);
    } else if (target > lastMonth) {
      setFirstMonth(addMonthKey(target, -(count - 1)));
    }
    setCursor(day);
  };

  const onDayActivate = (day: string) => {
    if (dayDisabled(day)) return;
    anchor.current = null;
    commit(activateDay(value, day));
  };

  const onSelectionStart = (day: string) => {
    if (dayDisabled(day)) return;
    anchor.current = day;
  };

  const onSelectionExtend = (day: string) => {
    if (disabled) return;
    const from = anchor.current ?? value.start ?? day;
    anchor.current = from;
    commit(rangeFromDrag(from, day));
  };

  const onSelectionEnd = (day: string) => {
    const from = anchor.current;
    anchor.current = null;
    /* A drag that wandered more than the threshold but never left its day is
       still one press on that day. */
    if (from && from === day) onDayActivate(day);
  };

  const onEscape = () => {
    anchor.current = null;
    if (value.start && !value.end) commit({ start: null, end: null });
  };

  const selection = value.start
    ? { start: value.start, end: value.end ?? value.start }
    : null;

  const dayLabel = (day: string): string => {
    const parts = [fullDayName(day)];
    if (day === today) parts.push('today');
    if (day === value.start) parts.push('check-in');
    if (day === value.end) parts.push('check-out');
    const mark = markOf(day, occupied);
    if (mark) parts.push(markText(mark));

    return parts.join(', ');
  };

  const renderDay = (day: string, ctx: { selected: boolean }) => {
    const mark = markOf(day, occupied);

    return (
      <>
        <DayNumber day={day} />
        <Mark
          aria-hidden="true"
          data-mark={mark ? mark.kind : undefined}
          $kind={mark ? mark.kind : null}
          $onFill={ctx.selected}
        />
      </>
    );
  };

  /* ONE tab stop for the pair: the grid that holds the cursor, else the
     first. */
  const cursorGrid = months.findIndex(month => month === monthOf(cursor));
  const tabGrid = cursorGrid === -1 ? 0 : cursorGrid;
  const hasValue = !!(value.start || value.end);

  return (
    <Root
      id={id}
      role="group"
      aria-labelledby={labelId}
      aria-describedby={statusId}
      className={className}
    >
      <Header>
        <Label id={labelId}>{label}</Label>
        {hasValue ? (
          <AppButton
            variant="ghost"
            size="compact"
            disabled={disabled}
            onClick={() => {
              anchor.current = null;
              commit({ start: null, end: null });
            }}
          >
            Clear dates
          </AppButton>
        ) : null}
      </Header>
      <Nav>
        <AppButton
          variant="ghost"
          size="compact"
          disabled={!canGoBack}
          onClick={() => setFirstMonth(prev => addMonthKey(prev, -1))}
        >
          Previous month
        </AppButton>
        <AppButton
          variant="ghost"
          size="compact"
          disabled={!canGoForward}
          onClick={() => setFirstMonth(prev => addMonthKey(prev, 1))}
        >
          Next month
        </AppButton>
      </Nav>
      <Months $count={months.length}>
        {months.map((month, index) => (
          <MonthGrid
            key={`grid-${index}`}
            month={month}
            today={today}
            headingId={`${id}-month-${index}`}
            headingLevel={3}
            showHeading
            density="picker"
            selectable={!disabled}
            selection={selection}
            renderDay={renderDay}
            dayLabel={dayLabel}
            focusDay={cursor}
            onFocusDayChange={onFocusDayChange}
            onDayActivate={onDayActivate}
            onSelectionStart={onSelectionStart}
            onSelectionExtend={onSelectionExtend}
            onSelectionEnd={onSelectionEnd}
            onEscape={onEscape}
            isDayDisabled={dayDisabled}
            inTabOrder={index === tabGrid}
          />
        ))}
      </Months>
      <Status id={statusId} aria-live="polite" aria-atomic="true">
        {liveText(value)}
      </Status>
      {conflicts.length > 0 ? (
        <ConflictLine role="alert" data-conflict="">
          {conflicts.map(range => (
            <span key={`${range.kind}-${range.start}-${range.end}-${range.label}`}>
              {conflictText(range)}
            </span>
          ))}
        </ConflictLine>
      ) : null}
      {warnings.length > 0 ? (
        <WarningLine data-warning="">
          {warnings.map(range => (
            <span key={`${range.kind}-${range.start}-${range.end}-${range.label}`}>
              {conflictText(range)}
            </span>
          ))}
        </WarningLine>
      ) : null}
      {zoneNote ? <ZoneNote>{zoneNote}</ZoneNote> : null}
    </Root>
  );
};
